import { useEffect, useState } from "react";
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconCheck,
  IconDownload,
  IconEdit,
  IconExternalLink,
  IconPlus,
  IconRefresh,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type {
  ProcurementAttachmentKind,
  PurchaseOrderDetail,
  PurchaseOrderStatus,
  Supplier,
} from "../api/types";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { ResponsiveTable } from "../components/responsiveTable/ResponsiveTable";

const KINDS: ProcurementAttachmentKind[] = ["invoice", "payment", "customs", "tracking", "other"];
const ALL_STATUSES: PurchaseOrderStatus[] = ["ordered", "in_transit", "at_customs", "received"];

function statusColor(s: PurchaseOrderStatus): string {
  return ({ ordered: "gray", in_transit: "blue", at_customs: "orange", received: "teal" } as const)[s];
}

function trackingStatusColor(s: string | null): string | undefined {
  if (!s) return undefined;
  if (s === "delivered") return "teal";
  if (s === "in_transit") return "blue";
  if (s === "returned") return "red";
  return "gray";
}

export function ProcurementDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const user = useCurrentUser();
  const isAdmin = user.role === "admin";
  const orderId = id ? Number(id) : NaN;

  const [data, setData] = useState<PurchaseOrderDetail | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);

  const [addLineOpen, setAddLineOpen] = useState(false);
  const [lineForm, setLineForm] = useState({ code: "", description: "", quantity: "", unit_price: "" });

  const [addTrackingOpen, setAddTrackingOpen] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingNote, setTrackingNote] = useState("");

  const [addAttachmentKind, setAddAttachmentKind] = useState<ProcurementAttachmentKind | null>(null);

  const [deleteOrderOpen, setDeleteOrderOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");

  async function reload() {
    if (!orderId) return;
    setLoading(true);
    try {
      const [d, sups] = await Promise.all([api.getOrder(orderId), api.listSuppliers()]);
      setData(d);
      setSuppliers(sups);
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, [orderId]);

  if (!data) return <Container size="lg"><Text c="dimmed">{loading ? t("common.loading") : t("procurement.notFound")}</Text></Container>;

  const order = data.order;
  const supplier = suppliers.find((s) => s.id === order.supplier_id);

  async function changeStatus(v: string | null) {
    if (!v) return;
    try {
      await api.updateOrder(order.id, { status: v as PurchaseOrderStatus });
      await reload();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function submitLine() {
    if (!lineForm.quantity) {
      notifications.show({ color: "red", message: t("procurement.line.quantityRequired") });
      return;
    }
    try {
      await api.createOrderLine(order.id, {
        code: lineForm.code.trim() || null,
        description: lineForm.description.trim() || null,
        quantity: Number(lineForm.quantity),
        unit_price: lineForm.unit_price ? Number(lineForm.unit_price) : null,
      });
      setAddLineOpen(false);
      setLineForm({ code: "", description: "", quantity: "", unit_price: "" });
      await reload();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function removeLine(lineId: number) {
    if (!confirm(t("procurement.line.confirmDelete"))) return;
    try {
      await api.deleteOrderLine(order.id, lineId);
      await reload();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function submitTracking() {
    if (!trackingNumber.trim()) return;
    try {
      await api.createOrderTracking(order.id, { tracking_number: trackingNumber.trim(), note: trackingNote.trim() || null });
      setAddTrackingOpen(false);
      setTrackingNumber("");
      setTrackingNote("");
      await reload();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function refreshTracking(tid: number) {
    try {
      await api.refreshOrderTracking(order.id, tid);
      await reload();
    } catch (e) {
      let msg = e instanceof ApiError ? e.message : String(e);
      if (e instanceof ApiError && (e.detail as { error?: string } | null)?.error === "track17_not_configured") {
        msg = t("procurement.tracking.notConfigured");
      }
      notifications.show({ color: "red", message: msg });
    }
  }

  async function removeTracking(tid: number) {
    if (!confirm(t("procurement.tracking.confirmDelete"))) return;
    try {
      await api.deleteOrderTracking(order.id, tid);
      await reload();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function uploadFile(kind: ProcurementAttachmentKind, file: File) {
    try {
      await api.uploadOrderAttachment(order.id, file, kind);
      await reload();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function removeAttachment(aid: number) {
    if (!confirm(t("procurement.attachment.confirmDelete"))) return;
    try {
      await api.deleteOrderAttachment(order.id, aid);
      await reload();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function confirmDeleteOrder() {
    setDeleting(true);
    try {
      await api.deleteOrder(order.id);
      setDeleteOrderOpen(false);
      navigate("/procurement");
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setDeleting(false);
    }
  }

  async function saveTitle() {
    try {
      await api.updateOrder(order.id, { title: titleDraft.trim() || null });
      setEditingTitle(false);
      await reload();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  function track17Url(num: string): string {
    return `https://t.17track.net/en#nums=${encodeURIComponent(num)}`;
  }

  return (
    <Container size="lg">
      <Anchor component={Link} to="/procurement" size="sm">← {t("procurement.backToList")}</Anchor>
      <Group justify="space-between" mt="sm" mb="md" wrap="wrap" align="flex-start">
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Title order={3}>{order.code} — {supplier?.name ?? `#${order.supplier_id}`}</Title>
          {editingTitle ? (
            <Group gap="xs" wrap="nowrap">
              <TextInput
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.currentTarget.value)}
                placeholder={t("procurement.titlePlaceholder")}
                style={{ flex: 1, maxWidth: 500 }}
                autoFocus
              />
              <ActionIcon color="teal" variant="subtle" onClick={() => void saveTitle()} title={t("common.save")}>
                <IconCheck size={16} />
              </ActionIcon>
              <ActionIcon variant="subtle" onClick={() => setEditingTitle(false)} title={t("common.cancel")}>
                <IconX size={16} />
              </ActionIcon>
            </Group>
          ) : (
            <Group gap="xs" wrap="nowrap">
              <Text c={order.title ? undefined : "dimmed"} size="md">
                {order.title || t("procurement.titlePlaceholder")}
              </Text>
              {isAdmin && (
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={() => {
                    setTitleDraft(order.title ?? "");
                    setEditingTitle(true);
                  }}
                  title={t("common.edit")}
                >
                  <IconEdit size={14} />
                </ActionIcon>
              )}
            </Group>
          )}
        </Stack>
        {isAdmin && (
          <Button
            color="red"
            variant="light"
            leftSection={<IconTrash size={16} />}
            onClick={() => setDeleteOrderOpen(true)}
          >
            {t("procurement.deleteOrder")}
          </Button>
        )}
      </Group>

      <Modal
        opened={deleteOrderOpen}
        onClose={() => setDeleteOrderOpen(false)}
        title={t("procurement.deleteOrder")}
        centered
      >
        <Stack>
          <Text>{t("procurement.confirm.deleteOrder", { code: order.code })}</Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setDeleteOrderOpen(false)} disabled={deleting}>
              {t("common.cancel")}
            </Button>
            <Button color="red" onClick={() => void confirmDeleteOrder()} loading={deleting}>
              {t("procurement.deleteOrder")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Card withBorder mb="md">
        <Group justify="space-between" wrap="wrap">
          <Stack gap={4}>
            <Group gap="md" wrap="wrap">
              <InfoBlock label={t("procurement.col.externalId")} value={order.supplier_order_id ?? "—"} />
              <InfoBlock label={t("procurement.col.orderedAt")} value={order.ordered_at} />
              <InfoBlock label={t("procurement.col.receivedAt")} value={order.received_at ?? "—"} />
              {isAdmin && (
                <InfoBlock
                  label={t("procurement.col.total")}
                  value={order.total_amount != null ? `${order.total_amount.toFixed(2)} ${order.total_currency ?? ""}` : "—"}
                />
              )}
            </Group>
            {order.notes && <Text size="sm" c="dimmed" mt="xs">{order.notes}</Text>}
          </Stack>
          <Stack gap="xs" align="end">
            {isAdmin ? (
              <Select
                data={ALL_STATUSES.map((s) => ({ value: s, label: t(`procurement.status.${s}`) }))}
                value={order.status}
                onChange={changeStatus}
                allowDeselect={false}
                w={200}
              />
            ) : (
              <Badge color={statusColor(order.status)} variant="light" size="lg">
                {t(`procurement.status.${order.status}`)}
              </Badge>
            )}
          </Stack>
        </Group>
      </Card>

      <Card withBorder mb="md">
        <Group justify="space-between" mb="xs">
          <Title order={5}>{t("procurement.linesTitle")}</Title>
          {isAdmin && (
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAddLineOpen(true)}>
              {t("procurement.line.add")}
            </Button>
          )}
        </Group>
        <ResponsiveTable
          mobile={{
            rows: data.lines,
            rowKey: (ln) => ln.id,
            title: (ln) => ln.code ?? "—",
            subtitle: (ln) => ln.description ?? "—",
            fields: [
              { label: t("procurement.line.quantity"), render: (ln) => ln.quantity },
              ...(isAdmin
                ? [{ label: t("procurement.line.unitPrice"), render: (ln: (typeof data.lines)[number]) => (ln.unit_price != null ? ln.unit_price.toFixed(4) : "—") }]
                : []),
            ],
            actions: isAdmin
              ? (ln) => (
                  <ActionIcon size="lg" color="red" variant="subtle" onClick={() => void removeLine(ln.id)} title={t("common.delete")}>
                    <IconTrash size={16} />
                  </ActionIcon>
                )
              : undefined,
            empty: t("procurement.line.empty"),
          }}
        >
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("procurement.line.code")}</Table.Th>
              <Table.Th>{t("procurement.line.description")}</Table.Th>
              <Table.Th>{t("procurement.line.quantity")}</Table.Th>
              {isAdmin && <Table.Th>{t("procurement.line.unitPrice")}</Table.Th>}
              {isAdmin && <Table.Th />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.lines.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={isAdmin ? 5 : 3}>
                  <Text c="dimmed" size="sm">{t("procurement.line.empty")}</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              data.lines.map((ln) => (
                <Table.Tr key={ln.id}>
                  <Table.Td>{ln.code ?? "—"}</Table.Td>
                  <Table.Td>{ln.description ?? "—"}</Table.Td>
                  <Table.Td>{ln.quantity}</Table.Td>
                  {isAdmin && <Table.Td>{ln.unit_price != null ? ln.unit_price.toFixed(4) : "—"}</Table.Td>}
                  {isAdmin && (
                    <Table.Td>
                      <ActionIcon color="red" variant="subtle" onClick={() => void removeLine(ln.id)} title={t("common.delete")}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
        </ResponsiveTable>
      </Card>

      <Card withBorder mb="md">
        <Group justify="space-between" mb="xs">
          <Title order={5}>{t("procurement.trackingTitle")}</Title>
          {isAdmin && (
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAddTrackingOpen(true)}>
              {t("procurement.tracking.add")}
            </Button>
          )}
        </Group>
        <Stack gap="xs">
          {data.trackings.length === 0 && <Text c="dimmed" size="sm">{t("procurement.tracking.empty")}</Text>}
          {data.trackings.map((tr) => (
            <Group key={tr.id} justify="space-between" wrap="nowrap" align="flex-start">
              <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                <Group gap="xs">
                  <Text ff="monospace" fw={600}>{tr.tracking_number}</Text>
                  <Badge color={trackingStatusColor(tr.status)} variant="light">
                    {tr.status ?? t("procurement.tracking.unknown")}
                  </Badge>
                </Group>
                {tr.last_location && (
                  <Text size="sm">
                    📍 {tr.last_location}
                    {tr.last_event_description ? ` — ${tr.last_event_description}` : ""}
                  </Text>
                )}
                {(tr.eta_from || tr.eta_to) && (
                  <Text size="sm" c="blue">
                    🎯 {t("procurement.tracking.eta")}:{" "}
                    {tr.eta_from && tr.eta_to && tr.eta_from !== tr.eta_to
                      ? `${tr.eta_from} → ${tr.eta_to}`
                      : (tr.eta_to ?? tr.eta_from)}
                  </Text>
                )}
                {tr.last_event_at && (
                  <Text size="xs" c="dimmed">
                    {t("procurement.tracking.lastEvent")}: {new Date(tr.last_event_at).toLocaleString()}
                  </Text>
                )}
                {tr.last_polled_at && (
                  <Text size="xs" c="dimmed">
                    {t("procurement.tracking.lastUpdate")}: {new Date(tr.last_polled_at).toLocaleString()}
                  </Text>
                )}
              </Stack>
              <Group gap="xs" wrap="nowrap">
                {isAdmin && (
                  <ActionIcon variant="subtle" onClick={() => void refreshTracking(tr.id)} title={t("procurement.tracking.refresh")}>
                    <IconRefresh size={14} />
                  </ActionIcon>
                )}
                <ActionIcon component="a" href={track17Url(tr.tracking_number)} target="_blank" rel="noreferrer" variant="subtle" title="17track">
                  <IconExternalLink size={14} />
                </ActionIcon>
                {isAdmin && (
                  <ActionIcon color="red" variant="subtle" onClick={() => void removeTracking(tr.id)} title={t("common.delete")}>
                    <IconTrash size={14} />
                  </ActionIcon>
                )}
              </Group>
            </Group>
          ))}
        </Stack>
      </Card>

      <Card withBorder mb="md">
        <Group justify="space-between" mb="xs">
          <Title order={5}>{t("procurement.attachmentsTitle")}</Title>
          {data.attachments.length > 0 && (
            <Button
              size="xs"
              variant="default"
              leftSection={<IconDownload size={14} />}
              component="a"
              href={api.orderAttachmentsZipUrl(order.id)}
            >
              {t("procurement.attachment.downloadZip")}
            </Button>
          )}
        </Group>
        <Stack gap="md">
          {KINDS.map((kind) => {
            const byKind = data.attachments.filter((a) => a.kind === kind);
            return (
              <div key={kind}>
                <Group justify="space-between" mb="xs">
                  <Text fw={600}>{t(`procurement.attachmentKind.${kind}`)}</Text>
                  {isAdmin && (
                    <Button size="xs" variant="default" leftSection={<IconPlus size={12} />} onClick={() => setAddAttachmentKind(kind)}>
                      {t("procurement.attachment.add")}
                    </Button>
                  )}
                </Group>
                {byKind.length === 0 ? (
                  <Text size="xs" c="dimmed">—</Text>
                ) : (
                  <Stack gap={4}>
                    {byKind.map((a) => (
                      <Group key={a.id} gap="xs" wrap="nowrap">
                        <Text size="sm" style={{ flex: 1 }}>{a.original_filename}</Text>
                        <Text size="xs" c="dimmed">{(a.size_bytes / 1024).toFixed(0)} KB</Text>
                        <ActionIcon component="a" href={api.orderAttachmentDownloadUrl(order.id, a.id)} variant="subtle" title={t("common.download")}>
                          <IconDownload size={14} />
                        </ActionIcon>
                        {isAdmin && (
                          <ActionIcon color="red" variant="subtle" onClick={() => void removeAttachment(a.id)} title={t("common.delete")}>
                            <IconTrash size={14} />
                          </ActionIcon>
                        )}
                      </Group>
                    ))}
                  </Stack>
                )}
              </div>
            );
          })}
        </Stack>
      </Card>

      <Modal opened={addLineOpen} onClose={() => setAddLineOpen(false)} title={t("procurement.line.add")} centered>
        <Stack>
          <TextInput label={t("procurement.line.code")} value={lineForm.code} onChange={(e) => setLineForm({ ...lineForm, code: e.currentTarget.value })} />
          <Textarea label={t("procurement.line.description")} value={lineForm.description} onChange={(e) => setLineForm({ ...lineForm, description: e.currentTarget.value })} minRows={2} />
          <Group grow>
            <NumberInput
              label={t("procurement.line.quantity")}
              value={lineForm.quantity}
              onChange={(v) => setLineForm({ ...lineForm, quantity: typeof v === "number" ? String(v) : (v ?? "") })}
              min={0}
              decimalScale={3}
              allowNegative={false}
              required
            />
            <NumberInput
              label={t("procurement.line.unitPrice")}
              value={lineForm.unit_price}
              onChange={(v) => setLineForm({ ...lineForm, unit_price: typeof v === "number" ? String(v) : (v ?? "") })}
              min={0}
              decimalScale={4}
              allowNegative={false}
            />
          </Group>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAddLineOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={() => void submitLine()}>{t("common.save")}</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={addTrackingOpen} onClose={() => setAddTrackingOpen(false)} title={t("procurement.tracking.add")} centered>
        <Stack>
          <TextInput label={t("procurement.tracking.number")} value={trackingNumber} onChange={(e) => setTrackingNumber(e.currentTarget.value)} required />
          <TextInput label={t("procurement.tracking.note")} value={trackingNote} onChange={(e) => setTrackingNote(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAddTrackingOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={() => void submitTracking()}>{t("common.save")}</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={addAttachmentKind !== null} onClose={() => setAddAttachmentKind(null)} title={t("procurement.attachment.add")} centered>
        <Stack>
          <Text size="sm" c="dimmed">{t("procurement.attachmentKind." + (addAttachmentKind ?? "other"))}</Text>
          <input
            type="file"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (f && addAttachmentKind) {
                await uploadFile(addAttachmentKind, f);
                setAddAttachmentKind(null);
              }
            }}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAddAttachmentKind(null)}>{t("common.cancel")}</Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

function InfoBlock({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm">{value}</Text>
    </div>
  );
}
